/**
 * Per-project cache of IRL document-match results. Each IRL request id
 * maps to up to ~5 candidate document matches we got back from the
 * `/api/irl/document-match` Claude call.
 *
 * Pure localStorage; pattern matches the other irl/* stores.
 */

const KEY = "__irlAnswers";

export type DocumentMatch = {
  /** Full path inside the SP root, e.g. "Financials/AR Aging/AR_Q3_2024.xlsx". */
  path: string;
  /** Just the file name, for display. */
  fileName: string;
  /** SharePoint webUrl so the row can deep-link out. May be empty. */
  webUrl: string;
  confidence: "high" | "medium" | "low";
  reasoning: string;
};

export type IRLAnswer = {
  matches: DocumentMatch[];
  /** Mode that produced these matches. Phase 1 = "structural" (names only). */
  mode: "structural" | "deep";
  lastChecked: string; // ISO
};

type AnswersByRequest = Record<string, IRLAnswer>;
type Map_ = Record<string, AnswersByRequest>;

function read(): Map_ {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Map_) : {};
  } catch {
    return {};
  }
}

function write(map: Map_): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(map));
  } catch {
    // ignore quota errors
  }
}

export function getIRLAnswers(projectId: string): AnswersByRequest {
  return read()[projectId] || {};
}

/** Replace a project's full answers map (used after a Find run). */
export function saveIRLAnswers(projectId: string, answers: AnswersByRequest): void {
  const map = read();
  map[projectId] = answers;
  write(map);
}

/** Whether this project currently has any document-match results saved. */
export function hasIRLAnswers(projectId: string): boolean {
  const map = readPersistedAnswers(projectId);
  return Object.keys(map).length > 0;
}

function readPersistedAnswers(projectId: string): Record<string, IRLAnswer> {
  return read()[projectId] || {};
}

export function clearIRLAnswers(projectId: string): void {
  const map = read();
  if (map[projectId]) {
    delete map[projectId];
    write(map);
  }
}
